import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Plus, X, CheckCircle2, XCircle, FileText, Trash2 } from "lucide-react";
import { DateInput } from "../components/DateInput";

const DOC_TYPES = [
  { v: "carte_pro", l: "Carte professionnelle" },
  { v: "ssiap", l: "Diplôme SSIAP" },
  { v: "identite", l: "Pièce d'identité" },
  { v: "contrat", l: "Contrat de travail" },
  { v: "autre", l: "Autre" },
];

function statusClass(s) {
  return { pending: "doc-pending", validated: "doc-validated", rejected: "doc-rejected", expired: "doc-expired" }[s] || "doc-pending";
}
function statusLabel(s) {
  return { pending:"En attente", validated:"Validé", rejected:"Refusé", expired:"Expiré" }[s] || s;
}

export default function Documents() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("status") || "all";
  const [filter, setFilter] = useState(initialFilter);
  const { lastEvent } = useRealtime();

  const load = async () => {
    const [d, a] = await Promise.all([http.get("/documents"), http.get("/agents")]);
    setItems(d.data); setAgents(a.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "document_changed" || lastEvent?.event === "agent_changed") load(); }, [lastEvent]);

  const filtered = filter === "all" ? items : items.filter((d)=>d.status===filter);
  const agentName = (id) => { const a = agents.find(x=>x.id===id); return a ? `${a.last_name} ${a.first_name}` : "—"; };

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="documents-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Documents</h1>
          <p className="text-sm text-slate-600">Validation des documents agents</p>
        </div>
        <button data-testid="add-document" onClick={()=>setCreating(true)} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4"/> Ajouter</button>
      </div>

      <div className="flex gap-2 mb-4 text-xs font-bold uppercase tracking-widest">
        {["all","pending","validated","rejected","expired"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={()=>setFilter(f)}
            className={`px-3 py-1.5 rounded-md border transition-colors ${filter===f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            {f==="all"?"Tous":statusLabel(f)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((d) => (
          <div key={d.id} className="border border-slate-200 bg-white rounded-md p-5 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 text-slate-400 flex-shrink-0"/>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{d.label}</div>
                  <div className="text-xs text-slate-500">{DOC_TYPES.find(t=>t.v===d.doc_type)?.l || d.doc_type}</div>
                </div>
              </div>
              <span className={`${statusClass(d.status)} rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-widest flex-shrink-0`}>{statusLabel(d.status)}</span>
            </div>
            <div className="text-sm">
              <div className="text-slate-500 text-xs">Agent</div>
              <div className="font-semibold">{agentName(d.agent_id)}</div>
            </div>
            {d.expires_on && <div className="text-xs text-slate-500">Expire le {new Date(d.expires_on).toLocaleDateString("fr-FR")}</div>}
            <div className="flex gap-2 mt-auto pt-2 border-t border-slate-100">
              <button data-testid={`validate-${d.id}`} onClick={async()=>{ await http.patch(`/documents/${d.id}/validate`, {status:"validated"}); load(); }} className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded-md py-1.5"><CheckCircle2 className="h-3.5 w-3.5"/>Valider</button>
              <button data-testid={`reject-${d.id}`} onClick={async()=>{ await http.patch(`/documents/${d.id}/validate`, {status:"rejected"}); load(); }} className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 rounded-md py-1.5"><XCircle className="h-3.5 w-3.5"/>Refuser</button>
              <button onClick={async()=>{ if(askDelete("Supprimer ?")){ await http.delete(`/documents/${d.id}`); load();} }} title={delTitle()} className="px-2 rounded-md hover:bg-slate-100"><Trash2 className="h-4 w-4 text-slate-500"/></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-slate-500 py-10 text-sm border border-dashed border-slate-200 rounded-md">Aucun document.</div>}
      </div>

      {creating && <DocModal agents={agents} onClose={()=>setCreating(false)} onSaved={load}/>}
    </div>
  );
}

function DocModal({ agents, onClose, onSaved }) {
  const [v, setV] = useState({ agent_id: agents[0]?.id || "", doc_type: "carte_pro", label: "", expires_on: "" });
  const [err, setErr] = useState("");
  const save = async () => {
    try { await http.post("/documents", { ...v, expires_on: v.expires_on || null }); onSaved(); onClose(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 animate-fade-in-up" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-xl font-bold">Nouveau document</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <L label="Agent *">
            <select data-testid="doc-agent" className="input" value={v.agent_id} onChange={(e)=>setV({...v, agent_id:e.target.value})}>
              {agents.map((a)=> <option key={a.id} value={a.id}>{a.last_name} {a.first_name}</option>)}
            </select>
          </L>
          <L label="Type *">
            <select className="input" value={v.doc_type} onChange={(e)=>setV({...v, doc_type:e.target.value})}>
              {DOC_TYPES.map((t)=> <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </L>
          <L label="Libellé *"><input data-testid="doc-label" className="input" value={v.label} onChange={(e)=>setV({...v, label:e.target.value})}/></L>
          <L label="Date d'expiration"><DateInput data-testid="doc-expires-on" className="input" value={v.expires_on} onChange={(e)=>setV({...v, expires_on:e.target.value})}/></L>
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="doc-save" onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md">Enregistrer</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}
function L({ label, children }) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }
